/**
 * Status, filter, and display config for holidays.
 */
import { Globe, MapPin, Building2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for a holiday type. */
export interface HolidayTypeConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  colorName: 'color-1' | 'color-2' | 'color-3';
}

/** Badge config for each holiday type. */
export const HOLIDAY_TYPE_CONFIG: Record<string, HolidayTypeConfig> = {
  national: {
    label: 'National',
    icon: Globe,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
    colorName: 'color-2',
  },
  regional: {
    label: 'Régional',
    icon: MapPin,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
    colorName: 'color-1',
  },
  establishment_specific: {
    label: 'Spécifique établissement',
    icon: Building2,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
    colorName: 'color-3',
  },
};

/** Holiday list filter state. */
export interface HolidayFilters {
  types: string[];
  dateRange?: 'all' | 'week' | 'month' | 'year';
  customDateFrom?: Date;
  customDateTo?: Date;
  vacationZoneId?: string;
}

/** Default holiday list filters. */
export const DEFAULT_HOLIDAY_FILTERS: HolidayFilters = {
  types: [],
  dateRange: 'all',
  customDateFrom: undefined,
  customDateTo: undefined,
};

/** Filter group IDs for the holidays {@link DataTable} configure panel. */
export type FilterGroupId = 'filter-type' | 'filter-period' | 'filter-date-range' | 'filter-zone';

/** Configure-panel order for holiday filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-type',
  'filter-period',
  'filter-date-range',
  'filter-zone',
];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-type': 'Type',
  'filter-period': 'Période',
  'filter-date-range': 'Date personnalisée',
  'filter-zone': 'Zone',
};

/** {@link Select} options for holiday type. */
export const HOLIDAY_TYPE_FORM_OPTIONS = Object.entries(HOLIDAY_TYPE_CONFIG).map(
  ([value, item]) => ({ value, label: item.label }),
);

export { DATE_RANGE_PERIOD_OPTIONS } from '@/constants/shared-catalog';

/** React Query keys invalidated after a holiday create / save. */
export const HOLIDAY_FORM_QUERY_KEYS = {
  list: ['holidays'] as const,
  one: (id: string) => ['holiday', id] as const,
  establishments: ['establishments'] as const,
  vacationZones: ['vacation-zones'] as const,
};
